import { type Location, normalizeVehicleSize } from '@cobliteam/common-types';
import { permissions, usePermissions } from '@cobliteam/permissions';
import type { Query } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useGetTriggers } from '../useEntity/useAlerts';
import { getTemplateChecklistsTotalCount } from '../useEntity/useChecklist/useChecklist';
import useCrm from '../useEntity/useCrm';
import { useGetDeviceIds } from '../useEntity/useDevices';
import useDriver from '../useEntity/useDriver';
import useFleet from '../useEntity/useFleet';
import { useGetPaginatedGeofences } from '../useEntity/useGeofenceV3';
import useInvite from '../useEntity/useInvite';
import useUser from '../useEntity/useUser';
import useVehicle from '../useEntity/useVehicle';
import useVehicleGroup from '../useEntity/useVehicleGroup';
import {
  ASSOCIATION_MIN_RATIO,
  MATURITY_AREAS,
  MATURITY_ITEM_TARGET,
  MATURITY_ITEMS,
  MATURITY_MIN_COUNT,
  type MaturityAreaKey,
  type MaturityItemId,
  type MaturityItemStatus,
  type MaturityNodeStatus,
} from './basicValueMaturityModel';

const FIVE_MINUTES = 5 * 60 * 1000;
// Janela em que um novo consumidor (a home montando logo depois do topbar no boot) reaproveita a contagem.
const REVALIDATE_ON_MOUNT_AFTER_MS = 30 * 1000;

/** Revalida no mount só contagens que não acabaram de chegar; outro consumidor montando no mesmo boot não refaz o pacote. */
export const revalidateMaturityOnMount = (query: Pick<Query, 'state'>): 'always' | false =>
  Date.now() - query.state.dataUpdatedAt > REVALIDATE_ON_MOUNT_AFTER_MS ? 'always' : false;

/**
 * Whether the vehicle has a defined porte the speed-by-porte flow can configure. The app-layer
 * `Vehicle.size` carries the pt-BR label ("Pequeno"…), so it must be normalized before checking —
 * comparing against the raw enum silently matched nothing and left the speed item vacuously done.
 */
const hasPorte = (size?: string): boolean => normalizeVehicleSize(size) !== undefined;

export interface MaturityItemState {
  id: MaturityItemId;
  area: MaturityAreaKey;
  route: Location;
  status: MaturityItemStatus;
  done: boolean;
  /** How many records the fleet currently has toward this item's goal (0 while the count is loading). */
  current: number;
  /** The goal the fleet must reach for this item to count as done (e.g. 2 of each). */
  target: number;
  /** Whether the user has permission to act on this item. Items the user can't act on are dropped from
   *  the list (and the BV score) entirely, so this is always true for the items returned here — it is
   *  kept as a stable flag for consumers that still gate on it. */
  allowed: boolean;
}

export interface MaturityAreaState {
  key: MaturityAreaKey;
  /** 1-based stage index among the areas the fleet can configure. */
  n: number;
  /** Node status for the stage track (done / current / locked). */
  status: MaturityNodeStatus;
  items: MaturityItemState[];
  doneCount: number;
  totalCount: number;
  pct: number;
}

export interface BasicValueMaturity {
  /** The applicable items in setup order (permission-gated and non-applicable steps are excluded). */
  items: MaturityItemState[];
  /** The three setup stages (areas with no item are omitted), each with track node status. */
  areas: MaturityAreaState[];
  /** The stage the fleet is currently working on (or the last stage when complete). */
  currentArea: MaturityAreaState;
  /** 1-based index of the current stage. */
  currentAreaIndex: number;
  /** How many stages are fully complete. */
  areasCompleted: number;
  /** The protagonist next step (first allowed, not-done item), or null when complete. */
  nextItem: MaturityItemState | null;
  doneCount: number;
  totalCount: number;
  /** Overall configuration percentage over all shown items. */
  overallPct: number;
  isComplete: boolean;
  isLoading: boolean;
  isError: boolean;
  fleetId?: string;
  /** Invalidate every entity count so the maturity reflects inline edits (used by the guided modal). */
  refetch: () => void;
}

interface UseBasicValueMaturityArgs {
  /** When false (e.g. the topbar ring behind a feature flag), the underlying queries don't fetch. */
  enabled?: boolean;
}

/**
 * Computes the fleet's setup maturity from the real entity-count queries, mirroring the gamified
 * "Central de Configuração da Frota" design: per-item status (done / next / todo), per-area and
 * the overall configuration percentage.
 *
 * Returns plain data only (no JSX / icons) so it can be shared by the page (apps/dashboard) and the
 * topbar avatar ring (libs/ui-business). The BV score is "steps done ÷ total applicable steps", so items
 * the user lacks permission to act on — like non-applicable steps — are excluded from the list and the
 * percentage (a fleet only ever needs to complete what it can act on to reach 100%).
 */
const useBasicValueMaturity = ({ enabled: enabledArg = true }: UseBasicValueMaturityArgs = {}): BasicValueMaturity => {
  const { session } = useUser();
  const { isAccessAllowed } = usePermissions();

  const fleetId = session?.fleet.id;
  const enabled = Boolean(fleetId) && enabledArg;

  // Shared, cache-friendly options: `staleTime` mantém o custo baixo quando o hook vive no topbar
  // sempre montado (lá o refetch acontece uma vez, no boot).
  //
  // Revalidar no mount é o que conserta o AE-2485: a etapa era criada FORA do guia (na página de
  // veículos, de motoristas etc.), nada invalidava essas contagens, e o guia continuava mostrando a
  // etapa como pendente por até 5 minutos. Como a página do guia e a home montam a cada navegação,
  // revalidar no mount deixa o progresso correto ao voltar; contagens de poucos segundos atrás são
  // reaproveitadas (`revalidateMaturityOnMount`). O `refetch()` explícito abaixo segue existindo para
  // as criações feitas dentro das modais, que precisam refletir na mesma montagem.
  const queryOptions = { enabled, staleTime: FIVE_MINUTES, refetchOnMount: revalidateMaturityOnMount, refetchOnWindowFocus: false } as const;

  const devicesQuery = useGetDeviceIds(queryOptions);
  const vehiclesQuery = useVehicle().getVehicles(queryOptions);
  const driversQuery = useDriver().getDriverIds({ options: queryOptions });
  const groupsQuery = useVehicleGroup().getVehicleGroups(queryOptions);
  const invitesQuery = useInvite().useGetPendingInvites(queryOptions);
  // Convite pendente é estado transitório: quando o convidado aceita, ele sai desta lista e entra na de
  // usuários da frota. Medir só os pendentes fazia a etapa "Convide usuários" DESCONCLUIR quando o time
  // aceitava (e nunca mais fechar, porque ninguém reconvida quem já está dentro), travando o guia abaixo
  // de 100%. Por isso a etapa mede o acervo: colegas já na conta + convites ainda pendentes.
  const fleetUsersQuery = useFleet().useGetFleetUsers(queryOptions);
  // Locais de interesse live in the Geofence V3 service (the source of truth the Geofences page reads);
  // the legacy GraphQL `geofences` query returns a different/empty set, so count via V3 totalElements.
  // retry:false: a geofence-service failure must fail fast (mirrors useGetAllGeofences) so it never keeps
  // the maturity in a long retry-loading state that could blank the page behind the guided modal.
  const geofencesQuery = useGetPaginatedGeofences({ filters: { page: 0, size: MATURITY_MIN_COUNT }, options: { ...queryOptions, retry: false } });
  // Gate on the alerts permission — without it the backend returns 403 on every topbar render
  // (GET /v1/triggers?page=0&size=2), flooding Datadog with error noise.
  const hasAlertsPermission = isAccessAllowed(permissions.CONFIG_AND_VIEW_CUSTOM_ALERTS);
  const triggersQuery = useGetTriggers({ page: 0, size: MATURITY_MIN_COUNT }, { ...queryOptions, enabled: queryOptions.enabled && hasAlertsPermission });
  // Gate on the checklist permission — without it the backend returns 403 on every topbar render,
  // flooding Datadog with GraphQLRequesterError noise (see RUM issue 00da1c60-6f28-11f1-a263-da7ad0900002).
  const hasChecklistPermission = isAccessAllowed(permissions.FULL_CONTROL_CHECKLIST);
  const checklistsQuery = getTemplateChecklistsTotalCount({ ...queryOptions, enabled: queryOptions.enabled && hasChecklistPermission });
  // Installation can't be self-served — it depends on a CRM scheduling ticket. Used only to GATE the
  // "devices" item (see below), so it stays out of countQueries: a CRM failure must not error the maturity.
  const schedulingTicketsQuery = useCrm().getSchedulingTickets({ options: queryOptions });

  // O modal guiado chama depois de cada criação inline (nem toda mutation invalida a própria entidade).
  // Refaz só as contagens daqui: invalidar ['DEVICES'], ['VEHICLES'] e ['DRIVERS'] refazia toda lista montada no app.
  const { refetch: refetchDevices } = devicesQuery;
  const { refetch: refetchVehicles } = vehiclesQuery;
  const { refetch: refetchDrivers } = driversQuery;
  const { refetch: refetchGroups } = groupsQuery;
  const { refetch: refetchInvites } = invitesQuery;
  const { refetch: refetchFleetUsers } = fleetUsersQuery;
  const { refetch: refetchGeofences } = geofencesQuery;
  const { refetch: refetchTriggers } = triggersQuery;
  const { refetch: refetchChecklists } = checklistsQuery;
  const refetch = useCallback(() => {
    if (!enabled) return;
    refetchDevices();
    refetchVehicles();
    refetchDrivers();
    refetchGroups();
    refetchInvites();
    refetchFleetUsers();
    refetchGeofences();
    if (hasAlertsPermission) refetchTriggers();
    if (hasChecklistPermission) refetchChecklists();
  }, [
    enabled,
    hasAlertsPermission,
    hasChecklistPermission,
    refetchDevices,
    refetchVehicles,
    refetchDrivers,
    refetchGroups,
    refetchInvites,
    refetchFleetUsers,
    refetchGeofences,
    refetchTriggers,
    refetchChecklists,
  ]);

  const countQueries = [devicesQuery, vehiclesQuery, driversQuery, groupsQuery, invitesQuery, fleetUsersQuery, geofencesQuery, triggersQuery, checklistsQuery];
  const isLoading = countQueries.some((query) => query.isLoading);
  const isError = countQueries.some((query) => query.isError);

  const devicesCount = devicesQuery.data?.length;
  const driversCount = driversQuery.data?.length;
  const groupsCount = groupsQuery.data?.length;
  const invitesCount = invitesQuery.data?.length;
  // O próprio usuário logado não conta como "convidado" — a meta é trazer o time.
  const teammatesCount = fleetUsersQuery.data?.filter((fleetUser) => fleetUser.id !== session?.user?.id).length;
  // Acervo da etapa "users": quem já está na conta + quem ainda tem convite pendente. `undefined`
  // enquanto qualquer uma das duas contagens carrega, para a etapa não piscar como pendente.
  const usersCount = invitesCount === undefined || teammatesCount === undefined ? undefined : invitesCount + teammatesCount;
  const geofencesCount = geofencesQuery.data?.totalElements;
  const alertsCount = triggersQuery.data?.totalElements;
  const checklistsCount = checklistsQuery.data;
  const vehicles = vehiclesQuery.data;
  // Whether the fleet has any installation scheduling ticket; only meaningful once the query resolves.
  const hasSchedulingTickets = (schedulingTicketsQuery.data?.total ?? 0) > 0;
  const schedulingTicketsResolved = !schedulingTicketsQuery.isLoading;

  return useMemo<BasicValueMaturity>(() => {
    const vehiclesWithDevice = vehicles?.filter((vehicle) => vehicle.deviceId).length;
    const vehiclesWithDriver = vehicles?.filter((vehicle) => vehicle.driverId).length;
    // "Registro de velocidade" = the vehicle's allowed speed (no over-speeding alarm). The speed-by-porte
    // flow only touches vehicles with a defined porte, so progress is measured over those: a fleet with no
    // porte has nothing to configure here (target 0 → done → the step never shows).
    const vehiclesWithPorte = vehicles?.filter((vehicle) => hasPorte(vehicle.size));
    const speedConfiguredWithPorte = vehiclesWithPorte?.filter((vehicle) => (vehicle.speedLimitInKmh ?? 0) > 0).length;

    // Each item resolves to how many records the fleet has (`current`) against the goal it must reach
    // (`target`). `current` is undefined while its count query is still loading. `done` is then a simple
    // `current >= target`, and the same numbers drive the "X/Y" progress shown in the item's circle.
    // Meta desenhada no Figma quando existe; senão, o mínimo genérico de antes.
    const goal = (id: MaturityItemId) => MATURITY_ITEM_TARGET[id] ?? MATURITY_MIN_COUNT;

    const evaluate = (id: MaturityItemId): { current?: number; target: number } => {
      switch (id) {
        case 'devices':
          return { current: devicesCount, target: goal('devices') };
        case 'vehicles':
          return { current: vehicles?.length, target: goal('vehicles') };
        // Device coverage: done once at least half the fleet's vehicles have a device (goal ceil(total ×
        // ratio)), so a small fleet whose vehicles are all equipped isn't stuck at an unreachable "2".
        case 'device-association':
          return { current: vehiclesWithDevice, target: vehicles ? Math.ceil(vehicles.length * ASSOCIATION_MIN_RATIO) : MATURITY_MIN_COUNT };
        case 'drivers':
          return { current: driversCount, target: goal('drivers') };
        // Driver coverage: done once at least half the fleet's vehicles have a driver assigned.
        // Goal is ceil(total × ratio), so the circle reads e.g. "3/5" toward that majority.
        case 'driver-association':
          return { current: vehiclesWithDriver, target: vehicles ? Math.ceil(vehicles.length * ASSOCIATION_MIN_RATIO) : MATURITY_MIN_COUNT };
        // Acervo, não estado transitório: colegas já na conta + convites pendentes (ver acima).
        case 'users':
          return { current: usersCount, target: goal('users') };
        case 'groups':
          return { current: groupsCount, target: goal('groups') };
        case 'alerts':
          return { current: alertsCount, target: goal('alerts') };
        case 'speed':
          return { current: speedConfiguredWithPorte, target: vehiclesWithPorte ? Math.ceil(vehiclesWithPorte.length * ASSOCIATION_MIN_RATIO) : MATURITY_MIN_COUNT };
        case 'geofences':
          return { current: geofencesCount, target: goal('geofences') };
        case 'checklists':
          return { current: checklistsCount, target: goal('checklists') };
      }
    };

    const isDone = (id: MaturityItemId): boolean => {
      const { current, target } = evaluate(id);
      return current !== undefined && current >= target;
    };

    // Installation depends on a CRM scheduling ticket. With no pending ticket there's nothing the user
    // can act on, so the "devices" item is dropped (not shown as a missing point) unless it's already
    // done. While the tickets query is still loading we keep it, to avoid hiding then re-showing it.
    const showInstallation = isDone('devices') || hasSchedulingTickets || !schedulingTicketsResolved;
    // The BV score is "steps done ÷ total applicable steps", so steps that don't apply must stay OUT of
    // the denominator (see AE-2436): items the user has no permission to act on are dropped entirely — a
    // fleet where checklists can't be configured, say, should still be able to reach 100%. Installation is
    // likewise only counted when actionable. This keeps a single, consistent set behind items / areas /
    // `overallPct` (no divergent scores).
    const visibleItems = MATURITY_ITEMS.filter((item) => (item.id !== 'devices' || showInstallation) && isAccessAllowed(item.permission));
    // The protagonist "next" step is simply the first not-done item (all visible items are actionable).
    const nextModel = visibleItems.find((item) => !isDone(item.id));

    // Progressão travada (AE-2498): dentro de cada área, só a primeira etapa pendente é acionável; as
    // seguintes ficam bloqueadas até ela fechar. O tooltip do Figma ("Finalize a tarefa anterior para
    // desbloquear essa") explica isso ao usuário na própria linha.
    const unlockedByArea = new Set(
      MATURITY_AREAS.map((areaKey) => visibleItems.find((item) => item.area === areaKey && !isDone(item.id))?.id).filter((id): id is MaturityItemId => id !== undefined)
    );

    const items: MaturityItemState[] = visibleItems.map((item) => {
      const { current, target } = evaluate(item.id);
      const done = current !== undefined && current >= target;
      let status: MaturityItemStatus = 'locked';
      if (done) status = 'done';
      else if (item.id === nextModel?.id) status = 'next';
      else if (unlockedByArea.has(item.id)) status = 'todo';
      return { id: item.id, area: item.area, route: item.route, status, done, current: current ?? 0, target, allowed: isAccessAllowed(item.permission) };
    });

    const areaBases = (['installation', 'setup', 'configuration'] as MaturityAreaKey[])
      .map((key) => {
        const areaItems = items.filter((item) => item.area === key);
        const doneCount = areaItems.filter((item) => item.done).length;
        const totalCount = areaItems.length;
        const complete = totalCount > 0 && doneCount === totalCount;
        return { key, items: areaItems, doneCount, totalCount, pct: totalCount === 0 ? 0 : Math.round((doneCount / totalCount) * 100), complete };
      })
      .filter((area) => area.totalCount > 0);

    // `currentArea` (the protagonist stage shown in the hero/nudge) is still the first not-yet-complete
    // stage. `n` is the 1-based position among present stages.
    const firstIncompleteArea = areaBases.find((area) => !area.complete);
    const currentAreaIndex = firstIncompleteArea ? areaBases.indexOf(firstIncompleteArea) + 1 : areaBases.length;
    // Track node state is per-area progress, NOT a sequential stepper: nothing done = locked
    // ("não iniciado"), some done = current ("em progresso"), all done = done ("concluído"). So more
    // than one stage can read as "in progress" at the same time.
    const areas: MaturityAreaState[] = areaBases.map((area, index) => {
      let status: MaturityNodeStatus = 'locked';
      if (area.complete) status = 'done';
      else if (area.doneCount > 0) status = 'current';
      return { key: area.key, n: index + 1, status, items: area.items, doneCount: area.doneCount, totalCount: area.totalCount, pct: area.pct };
    });
    const areasCompleted = areas.filter((area) => area.status === 'done').length;
    const currentArea = areas.find((area) => area.n === currentAreaIndex) ?? areas[areas.length - 1];

    const doneCount = items.filter((item) => item.done).length;
    const totalCount = items.length;
    const overallPct = totalCount === 0 ? 100 : Math.round((doneCount / totalCount) * 100);
    const isComplete = overallPct === 100;
    const nextItem = items.find((item) => item.status === 'next') ?? null;

    return {
      items,
      areas,
      currentArea,
      currentAreaIndex,
      areasCompleted,
      nextItem,
      doneCount,
      totalCount,
      overallPct,
      isComplete,
      isLoading,
      isError,
      fleetId,
      refetch,
    };
  }, [
    vehicles,
    devicesCount,
    driversCount,
    groupsCount,
    usersCount,
    geofencesCount,
    alertsCount,
    checklistsCount,
    hasSchedulingTickets,
    schedulingTicketsResolved,
    isAccessAllowed,
    isLoading,
    isError,
    fleetId,
    refetch,
  ]);
};

export default useBasicValueMaturity;
